import * as c from "purify-ts/Codec";
import h from "trader-hyperscript";
import { Source, unsubber } from "trader.ts/types/source";
import { dyn, scheduleForCleanup } from "trader.ts/ui";
import {
  datebox,
  selectbox,
  textbox,
  wrapWithLabel,
} from "trader.ts/ui.common";
import { mapPartial, tryExtractErrorMessage } from "trader.ts/utils";
import { match } from "ts-pattern";
import { isNil } from "../lib/istype";
import { parseFloatSafe } from "../lib/parsefloatsafe";
import * as FA from "./../../styles/fontawesome.css.classes";
import * as style from "./../../styles/style.css.classes";
import * as joda from "@js-joda/core";
import { Either, Left, Right } from "purify-ts/Either";

export type Filter<FilterTarget> = {
  render: () => HTMLElement | HTMLElement[];
  filter: (t: FilterTarget) => boolean;
  serializeState: () => unknown;
  applySerializedState: (s: unknown) => boolean;
  observe: (cb: () => void) => unsubber;
};

export function lens<Small, Big>(
  zoom: (b: Big) => Small,
  smallFilter: Filter<Small>,
): Filter<Big> {
  return {
    ...smallFilter,
    filter: (t: Big) => smallFilter.filter(zoom(t)),
  };
}

export type CompositeFilterSpecs<FilterTarget> = {
  [key: string]: { label: string; mkFilter: () => Filter<FilterTarget> };
};

export function mkCompositeFilter<FilterTarget>(
  specs: CompositeFilterSpecs<FilterTarget>,
): Filter<FilterTarget> {
  // TODO: memory leak
  // We need to move all observe() calls into the render() call
  // and then schedule cleanup funcs to call clearFullCompositeFilter()
  let observers: (() => void)[] = [];

  function runObservers() {
    observers.forEach((obs) => obs());
  }

  const operatorS = new Source("and" as "and" | "or");
  const activeFiltersS: Source<{
    first: {
      key: Source<string>;
      keyUnsubber: unsubber;
      filter: Source<null | {
        filter: Filter<FilterTarget>;
        unsubber: unsubber;
      }>;
      next: {
        key: Source<string>;
        keyUnsubber: unsubber;
        filter: Source<null | {
          filter: Filter<FilterTarget>;
          unsubber: unsubber;
        }>;
      }[];
    } | null;
  }> = new Source({
    operator: operatorS,
    first: null,
  });

  function addNewFilter() {
    const newF = {
      key: new Source(""),
      filter: new Source(null) as Source<null | {
        filter: Filter<FilterTarget>;
        unsubber: unsubber;
      }>,
    };
    let oldkey = "";
    // TODO we need to clean this observer up somewhere!!!
    const keyUnsubber = newF.key.observe((newkey) => {
      if (oldkey !== newkey) {
        oldkey = newkey;

        const foundSpec = specs[newkey];
        if (foundSpec) {
          const createdFilter = foundSpec.mkFilter();
          const unsubber = createdFilter.observe(runObservers);
          newF.filter.set({
            filter: createdFilter,
            unsubber,
          });
        } else {
          newF.filter.set(null);
        }
        runObservers();
      }
    });
    const curr = activeFiltersS.get();

    if (curr.first === null) {
      activeFiltersS.set({
        first: {
          key: newF.key,
          keyUnsubber,
          filter: newF.filter,
          next: [],
        },
      });
    } else {
      activeFiltersS.set({
        first: {
          key: curr.first.key,
          keyUnsubber: curr.first.keyUnsubber,
          filter: curr.first.filter,
          next: curr.first.next.concat([
            {
              key: newF.key,
              keyUnsubber,
              filter: newF.filter,
            },
          ]),
        },
      });
    }

    return newF;
  }

  function removeFilter(
    target: { tag: "first" } | { tag: "next"; i: number },
    // replacement: null | {
    //   operator: "and" | "or";
    //   key: Source<string>;
    //   filter: Source<null | {
    //     filter: Filter<FilterTarget>;
    //     unsubber: unsubber[];
    //   }>;
    // },
  ) {
    const activeFilter = activeFiltersS.get();

    match(target)
      .with({ tag: "first" }, () => {
        const f = activeFilter.first;
        if (f) {
          f.keyUnsubber();
          const instance = f.filter.get();
          if (!isNil(instance)) {
            instance.unsubber();
          }
          const nextInLine = f.next[0];
          if (!nextInLine) {
            activeFiltersS.set({
              first: null,
            });
          } else {
            activeFiltersS.set({
              first: {
                key: nextInLine.key,
                keyUnsubber: nextInLine.keyUnsubber,
                filter: nextInLine.filter,
                // next: replacement === null ? f.next.slice(1) : f.next,
                next: f.next.slice(1),
              },
            });
          }
        }
      })
      .with({ tag: "next" }, ({ i }) => {
        const f = activeFilter.first;
        if (f) {
          const instance = f.next[i].filter.get();
          if (!isNil(instance)) {
            instance.unsubber();
          }
          activeFiltersS.set({
            first: {
              ...f,
              next: f.next
                .slice(0, i)
                // .concat(replacement ? [replacement] : [])
                .concat(f.next.slice(i + 1)),
            },
          });
        }
      })
      .exhaustive();

    if (activeFilter.first?.next.length === 1) {
      operatorS.set("and");
    }

    observers.forEach((obs) => obs());
  }

  function render() {
    scheduleForCleanup(operatorS.observe(runObservers));
    function renderSingle(
      f: {
        key: Source<string>;
        filter: Source<null | {
          filter: Filter<FilterTarget>;
          unsubber: unsubber;
        }>;
      },
      removeTarget: { tag: "first" } | { tag: "next"; i: number },
    ) {
      function renderOp(op: "and" | "or") {
        return match(op)
          .with("and", () => "En")
          .with("or", () => "Of")
          .exhaustive();
      }

      return (
        <div style className={style.composite_filter}>
          <div style={"min-width: 80px;"}>
            {match(removeTarget)
              .with({ tag: "first" }, () => (
                <div
                  className={style.input_like}
                  style="background-color: transparent;"
                >
                  {"Waar"}
                </div>
              ))
              .with({ tag: "next", i: 0 }, () =>
                selectbox({
                  source: operatorS,
                  options: [
                    { value: "and", label: renderOp("and") },
                    { value: "or", label: renderOp("or") },
                  ],
                }),
              )
              .with({ tag: "next" }, () => (
                <div
                  className={style.input_like}
                  style="background-color: transparent;"
                >
                  {dyn(operatorS, (op) => (
                    <span>{renderOp(op)}</span>
                  ))}
                </div>
              ))
              .exhaustive()}
          </div>
          <div style="max-width: 160px;">
            {selectbox({
              source: f.key,
              options: Object.entries(specs)
                .map(([key, spec]) => ({
                  value: key,
                  label: spec.label,
                }))
                .orderBy((a) => a.label),
              style: "width: 160px",
            })}
          </div>
          {dyn(f.filter, (fil) =>
            fil === null ? (
              <div>
                <div className={style.input_like}></div>
              </div>
            ) : (
              fil.filter.render()
            ),
          )}
          <button
            className={style.button_small + style.button_has_only_icon}
            style="max-width: 48px; height: var(--input-height); min-width: unset;"
            onclick={() => removeFilter(removeTarget)}
          >
            <i className={FA.icon_trash_alt_solid}></i>
          </button>
        </div>
      );
    }

    return (
      <div className={style.composite_filter_container}>
        {dyn(activeFiltersS, (activeFilters) => {
          if (activeFilters.first === null) {
            return (
              <button
                onclick={addNewFilter}
                style="width: 100%"
                className={style.button_em_down}
              >
                {"Maak nieuwe filter"}
              </button>
            );
          } else {
            return (
              <div>
                {renderSingle(activeFilters.first, { tag: "first" })}
                {activeFilters.first.next.map((f, i) =>
                  renderSingle(f, { tag: "next", i }),
                )}
                <button
                  onclick={addNewFilter}
                  style="width: 100%"
                  className={
                    style.button_em_down +
                    style.button_small +
                    style.button_has_only_icon
                  }
                >
                  <i className={FA.icon_plus_solid}></i>
                </button>
              </div>
            );
          }
        })}
      </div>
    );
  }
  function filter(t: FilterTarget) {
    const activeFilters = activeFiltersS.get();
    const op = operatorS.get();
    if (activeFilters.first === null) {
      return true;
    } else {
      const firstFilter = activeFilters.first.filter.get();
      const firstRes = firstFilter ? firstFilter.filter.filter(t) : true;
      return activeFilters.first.next.reduce((acc, next) => {
        const nextFilter = next.filter.get();
        const nextRes = isNil(nextFilter) ? true : nextFilter.filter.filter(t);
        return match(op)
          .with("and", () => acc && nextRes)
          .with("or", () => acc || nextRes)
          .exhaustive();
      }, firstRes);
    }
  }

  const codec = c.Codec.interface({
    operator: c.oneOf([c.exactly("and"), c.exactly("or")]),
    filters: c.array(
      c.Codec.interface({
        key: c.string,
        filter: c.unknown,
      }),
    ),
  });
  function serializeState() {
    const arr: {
      operator: "and" | "or";
      key: string;
      filter: unknown;
    }[] = [];
    const curr = activeFiltersS.get();
    const first = curr.first || null;
    const currFirstfilter = first?.filter.get() || null;
    if (first && currFirstfilter) {
      arr.push({
        operator: "and",
        key: first.key.get(),
        filter: currFirstfilter.filter.serializeState(),
      });
    }
    for (const next of curr.first?.next || []) {
      const currF = next.filter.get() || null;
      if (currF) {
        arr.push({
          operator: "and",
          key: next.key.get(),
          filter: currF.filter.serializeState(),
        });
      }
    }
    return codec.encode({
      operator: operatorS.get(),
      filters: arr,
    });
  }

  function clearFullCompositeFilter() {
    operatorS.set("and");
    const curr = activeFiltersS.get();
    if (isNil(curr.first)) {
      return;
    }
    for (let i = curr.first.next.length; i--; i >= 0) {
      removeFilter({ tag: "next", i });
    }
    removeFilter({ tag: "first" });
  }

  function applySerializedState(s: unknown) {
    clearFullCompositeFilter();
    const partiallyDecoded = codec.decode(s);
    return partiallyDecoded.caseOf({
      Left: (_) => false,
      Right: (r) => {
        operatorS.set(r.operator);
        for (const row of r.filters) {
          const spec = Object.entries(specs).find(([key]) => key === row.key);
          if (!spec) {
            return false;
          }
          const newF = addNewFilter();
          newF.key.set(spec[0]);
          const newFilterInstance = newF.filter.get();
          if (newFilterInstance === null) {
            throw new Error("Impossible");
          }
          const res = newFilterInstance.filter.applySerializedState(row.filter);

          if (!res) {
            return false;
          }
        }
        return true;
      },
    });
  }

  function observe(cb: () => void) {
    observers.push(cb);
    return () => {
      /* eslint-disable */
      observers = observers.filter((u) => u !== cb);
      /* eslint-enable */
    };
  }

  return {
    render,
    filter,
    serializeState,
    applySerializedState,
    observe,
  };
}

export function multiSelectFilter<Option, FilterTarget extends string>(opts: {
  values: {
    value: Option;
    label: string;
    shortLabel?: string;
    id: FilterTarget;
  }[];
  render?: {
    maxValuesOnButtonBeforeAbbrev?: number;
  };
}): Filter<FilterTarget> {
  type op = "eq" | "neq";
  const opS: Source<op> = new Source("eq");
  const selectedIdsS: Source<FilterTarget[]> = new Source([]);

  function filterImpl(
    t: FilterTarget,
    op: "eq" | "neq",
    selectedValues: FilterTarget[],
  ) {
    return match(op)
      .with("eq", () => selectedValues.includes(t))
      .with("neq", () => !selectedValues.includes(t))
      .exhaustive();
  }

  function filter(f: FilterTarget) {
    return filterImpl(f, opS.get(), selectedIdsS.get());
  }

  function render() {
    const op = selectbox({
      source: opS,
      options: [
        { value: "eq" as const, label: "is" },
        { value: "neq" as const, label: "is niet" },
      ],
    });

    const maxValuesOnButtonBeforeAbbrev =
      opts.render?.maxValuesOnButtonBeforeAbbrev || 2;

    const ms = multiSelectBoxClassy({
      source: selectedIdsS,
      options: opts.values.map((v) => ({
        value: v.id,
        label: v.label,
        shortlabel: v.shortLabel,
      })),
      render: {
        popupClass: style.popup_dropdown + style.multiselect_dropdown,
        popupStyle: "top: var(--input-height); min-width: 280px;",
        checkboxClass: style.checkbox + style.input_like,
        searchbox: (searchS) =>
          textbox({
            source: searchS,
            class: style.input_like,
          }),
        button: (onclick, selected) => {
          return (
            <button
              onclick={onclick}
              className={
                style.select_like + style.button_em_down + style.input_like
              }
              style="width: 100%;"
            >
              {selected.length === 0 ? (
                <span style="font-style: italic;">
                  {"Niets geselecteerd"}
                </span>
              ) : selected.length === 1 ? (
                selected[0].label
              ) : selected.length <= maxValuesOnButtonBeforeAbbrev ? (
                selected.map((s) => s.shortlabel || s.label).join(", ")
              ) : (
                <span style="font-style: italic;">
                  {"%n geselecteerd".replace(
                    "%n",
                    selected.length.toString(),
                  )}
                </span>
              )}
            </button>
          );
        },
      },
    });
    return (
      <div className={style.equal_columns}>
        <div style="max-width: 104px;">{op}</div>
        {ms}
      </div>
    );
  }

  const codec = c.Codec.interface({
    ids: c.array(c.string),
    op: c.oneOf([c.exactly("eq"), c.exactly("neq")]),
  });

  function observe(cb: () => void): unsubber {
    const unsub1 = opS.observe(cb);
    const unsub2 = selectedIdsS.observe(cb);

    return () => {
      unsub1();
      unsub2();
    };
  }

  return {
    render,
    filter,
    observe,
    serializeState: () =>
      codec.encode({ ids: selectedIdsS.get(), op: opS.get() }),
    applySerializedState: (s) => {
      return codec.decode(s).caseOf({
        Left: (_) => false,
        Right: (r) => {
          opS.set(r.op);
          selectedIdsS.set(
            (r.ids as FilterTarget[]).filter((id) =>
              opts.values.some((v) => (v.id as string) === (id as string)),
            ),
          );
          return true;
        },
      });
    },
  };
}

export function multiSelectBoxClassy<
  OPTION extends { value: string; label: string },
>(opts: {
  source: Source<string[]>;
  error?: Source<string | null>;
  options: OPTION[];
  class?: string;
  label?: string;
  textIfNothingSelected?: string;
  render?: {
    button?: (
      onclick: (ev: MouseEvent) => void,
      selected: OPTION[],
    ) => HTMLElement | HTMLElement[];
    searchbox?: (s: Source<string>) => HTMLElement | HTMLElement[];
    popupStyle?: string;
    popupClass?: string;
    checkboxClass?: string;
    checkboxStyle?: string;
  };
}) {
  const showPopupS: Source<null | number> = new Source(null);

  function findParentButton(el: HTMLElement | null): HTMLButtonElement | null {
    if (el === null) {
      return null;
    }
    if (el.tagName.toLowerCase() === "button") {
      return el as HTMLButtonElement;
    } else {
      return !el.parentElement ? null : findParentButton(el.parentElement);
    }
  }

  const buttonText = dyn(opts.source, function (current) {
    const text = current
      .map((co) => opts.options.find((o) => co === o.value))
      .filter((o) => o !== undefined)
      .map((o) => o?.label)
      .join(", ");
    return <span>{text === "" ? opts.textIfNothingSelected || "" : text}</span>;
  });

  function onButtonClick(ev: MouseEvent) {
    // When clicking on the text, the target of the event is not this button so we need to find it first
    const button = findParentButton((ev.target as HTMLElement) || null);
    if (button) {
      showPopupS.set(button.getBoundingClientRect().width || 180);
    }
  }

  const button = dyn(opts.source, (current) => {
    const selected = current
      .map((co) => opts.options.find((o) => co === o.value))
      .filter((o) => o !== undefined);
    if (opts.render?.button) {
      return opts.render.button(onButtonClick, selected);
    }
    return (
      <button className="multiselect-button" onclick={onButtonClick}>
        {buttonText}
      </button>
    );
  });

  function renderCheckbox(o: { value: string; label: string }) {
    const c = (
      <input
        type="checkbox"
        onchange={(e: InputEvent) => {
          const checked = (e?.target as HTMLInputElement)?.checked;
          const current = opts.source.get();
          const filtered = current.filter((c) => c !== o.value);
          opts.source.set(checked ? filtered.concat(o.value) : filtered);
        }}
      />
    ) as HTMLInputElement;
    const current = opts.source.get();
    const checked = current.some((c) => c === o.value);
    if (checked === true) {
      c.checked = true;
    }
    return (
      <div>
        <label
          className={opts.render?.checkboxClass || ""}
          style={opts.render?.checkboxStyle}
        >
          {c} {o.label}
        </label>
      </div>
    );
  }

  const searchS = new Source("");

  const searchBar = opts.render?.searchbox
    ? opts.render.searchbox(searchS)
    : textbox({
        source: searchS,
        class: "multiselect-popup-search",
      });

  const popup = dyn(showPopupS, function (showPopup) {
    if (showPopup !== null) {
      setTimeout(
        () =>
          Array.isArray(searchBar)
            ? searchBar.forEach((el) => el.focus && el.focus())
            : searchBar.focus(),
        1,
      );

      return (
        <div>
          <div
            style={
              "z-index: 101; " +
              "width: " +
              showPopup +
              "px;" +
              (opts.render?.popupStyle || "")
            }
            className={"multiselect-popup " + (opts.render?.popupClass || "")}
          >
            <div style="display: flex; align-items: center">
              {selectAll}
              <div style="flex: 1" className={style.input_with_icon}>
                <i className={FA.icon_search} style="top: -4px;"></i>
                {searchBar}
              </div>
            </div>
            <div className="multiselect-popup-checkboxes">
              {dyn(searchS, function (_search) {
                const search = _search.toLowerCase();
                return opts.options.map((o) =>
                  o.label.toLowerCase().includes(search) ? (
                    renderCheckbox(o)
                  ) : (
                    <span></span>
                  ),
                );
              })}
            </div>
          </div>
        </div>
      );
    } else {
      return <span></span>;
    }
  });

  const full = (
    <div style="position: relative">
      {button}
      {popup}
    </div>
  );

  const closePopup = (ev: MouseEvent) => {
    const containerEl = full;
    if (containerEl && !containerEl.contains(ev.target as Node)) {
      showPopupS.set(null);
    }
  };

  window.addEventListener("click", closePopup);
  scheduleForCleanup(() => {
    window.removeEventListener("click", closePopup);
  });

  const selectAll = (
    <input
      type="checkbox"
      id="select-all"
      onchange={(_e: InputEvent) => {
        debugger;
        const cbs = Array.from(
          full.querySelectorAll(`input[type="checkbox"]`),
        ).filter((cb) => cb.id !== "select-all");
        const search = searchS.get();

        if (cbs.some((cb) => (cb as HTMLInputElement).checked === false)) {
          cbs.forEach((c) => ((c as HTMLInputElement).checked = true));
          (selectAll as HTMLInputElement).checked = true;
          opts.source.set(
            mapPartial(opts.options, (o) =>
              o.label.toLowerCase().includes(search) ? o.value : null,
            ),
          );
        } else {
          cbs.forEach((c) => ((c as HTMLInputElement).checked = false));
          // (selectAll as HTMLInputElement).checked = false;
          opts.source.set([]);
        }
      }}
    />
  );

  return wrapWithLabel(opts.label, opts.error, full);
}

export function booleanFilter(opts: {}): Filter<boolean> {
  const boolS: Source<"true" | "false"> = new Source("true");

  function filter(f: boolean) {
    return match(boolS.get())
      .with("true", () => f === true)
      .with("false", () => f === false)
      .exhaustive();
  }

  function render() {
    const box = selectbox({
      source: boolS,
      options: [
        { value: "true" as const, label: "Ja" },
        { value: "false" as const, label: "Nee" },
      ],
    });

    return box;
  }

  const codec = c.Codec.interface({
    bool: c.oneOf([c.exactly("true"), c.exactly("false")]),
  });

  function observe(cb: () => void): unsubber {
    const unsub1 = boolS.observe(cb);

    return () => {
      unsub1();
    };
  }

  return {
    render,
    filter,
    observe,
    serializeState: () => {
      const bool = boolS.get();
      return codec.encode({ bool });
    },
    applySerializedState: (s) => {
      return codec.decode(s).caseOf({
        Left: (_) => false,
        Right: (r) => {
          boolS.set(r.bool);
          return true;
        },
      });
    },
  };
}

export function dateNullableFilter(opts: {
  showIsEmptyOperator: boolean;
}): Filter<joda.LocalDate | null> {
  type op = "null" | "eq" | "neq" | "gt" | "gte" | "lt" | "lte";
  const opS: Source<op> = new Source("eq");
  const valS: Source<joda.LocalDate | null> = new Source(joda.LocalDate.now());

  function filterImpl(
    t: joda.LocalDate | null,
    op: op,
    val: null | joda.LocalDate,
  ): boolean {
    return !!match(op)
      .with("null", () => t === null)
      .with("eq", () => t && val && t.equals(val))
      .with("neq", () => t && val && !t.equals(val))
      .with("gt", () => t && val && t.isAfter(val))
      .with("gte", () => t && val && (t.isAfter(val) || t.isEqual(val)))
      .with("lt", () => t && val && t.isBefore(val))
      .with("lte", () => t && val && (t.isBefore(val) || t.isEqual(val)))
      .exhaustive();
  }

  function filter(f: joda.LocalDate | null) {
    return filterImpl(f, opS.get(), valS.get());
  }

  function render() {
    const op = selectbox({
      source: opS,
      options: [
        ...(opts.showIsEmptyOperator
          ? [
              {
                value: "null" as const,
                label: "leeg",
              },
            ]
          : []),
        { value: "eq" as const, label: "=" },
        { value: "neq" as const, label: "/=" },
        { value: "gt" as const, label: ">" },
        { value: "gte" as const, label: ">=" },
        { value: "lt" as const, label: "<" },
        { value: "lte" as const, label: "<=" },
      ],
    });

    return (
      <div className={style.equal_columns}>
        <div style="max-width: 104px;">{op}</div>
        {dyn(opS, (op) => (op === "null" ? [] : datebox({ source: valS })))}
      </div>
    );
  }

  const codec = c.Codec.interface({
    val: c.nullable(localDateC),
    op: c.oneOf([
      c.exactly("null"),
      c.exactly("eq"),
      c.exactly("neq"),
      c.exactly("gt"),
      c.exactly("gte"),
      c.exactly("lt"),
      c.exactly("lte"),
    ]),
  });

  function observe(cb: () => void): unsubber {
    const unsub1 = opS.observe(cb);
    const unsub2 = valS.observe(cb);

    return () => {
      unsub1();
      unsub2();
    };
  }

  return {
    render,
    filter,
    observe,
    serializeState: () => {
      return codec.encode({ val: valS.get(), op: opS.get() });
    },
    applySerializedState: (s) => {
      return codec.decode(s).caseOf({
        Left: (_) => false,
        Right: (r) => {
          opS.set(r.op);
          valS.set(r.val);
          return true;
        },
      });
    },
  };
}

export function numberFilter<
  FilterTarget extends number,
>(opts: {}): Filter<FilterTarget> {
  type op = "eq" | "neq" | "gt" | "gte" | "lt" | "lte";
  const opS: Source<op> = new Source("eq");
  const numberS = new Source("0");

  function filterImpl(t: FilterTarget, op: op, num: number) {
    return match(op)
      .with("eq", () => (t as number) === num)
      .with("neq", () => (t as number) !== num)
      .with("gt", () => (t as number) > num)
      .with("gte", () => (t as number) >= num)
      .with("lt", () => (t as number) < num)
      .with("lte", () => (t as number) <= num)
      .exhaustive();
  }

  function filter(f: FilterTarget) {
    const parsed = parseFloatSafe(numberS.get());
    return filterImpl(f, opS.get(), isNil(parsed) ? 0 : parsed);
  }

  function render() {
    const op = selectbox({
      source: opS,
      options: [
        { value: "eq" as const, label: "=" },
        { value: "neq" as const, label: "/=" },
        { value: "gt" as const, label: ">" },
        { value: "gte" as const, label: ">=" },
        { value: "lt" as const, label: "<" },
        { value: "lte" as const, label: "<=" },
      ],
    });

    const ms = textbox({ type: "number", source: numberS });

    return (
      <div className={style.equal_columns}>
        <div style="max-width: 104px;">{op}</div>
        {ms}
      </div>
    );
  }

  const codec = c.Codec.interface({
    num: c.number,
    op: c.oneOf([
      c.exactly("eq"),
      c.exactly("neq"),
      c.exactly("gt"),
      c.exactly("gte"),
      c.exactly("lt"),
      c.exactly("lte"),
    ]),
  });

  function observe(cb: () => void): unsubber {
    const unsub1 = opS.observe(cb);
    const unsub2 = numberS.observe(cb);

    return () => {
      unsub1();
      unsub2();
    };
  }

  return {
    render,
    filter,
    observe,
    serializeState: () => {
      const number = parseFloatSafe(numberS.get());
      const num = isNil(number) ? 0 : number;
      return codec.encode({ num, op: opS.get() });
    },
    applySerializedState: (s) => {
      return codec.decode(s).caseOf({
        Left: (_) => false,
        Right: (r) => {
          opS.set(r.op);
          numberS.set(r.num.toString());
          return true;
        },
      });
    },
  };
}

export function stringFilter<
  FilterTarget extends string,
>(opts: {}): Filter<FilterTarget> {
  type op = "eq" | "neq" | "includes" | "notincludes";
  const opS: Source<op> = new Source("includes");
  const inputStrS = new Source("");

  function filterImpl(t: FilterTarget, op: op, inputStr: string) {
    return match(op)
      .with("eq", () => t.toLowerCase() === inputStr)
      .with("neq", () => t.toLowerCase() !== inputStr)
      .with("includes", () => t.toLowerCase().includes(inputStr))
      .with("notincludes", () => !t.toLowerCase().includes(inputStr))
      .exhaustive();
  }

  function filter(f: FilterTarget) {
    return filterImpl(f, opS.get(), inputStrS.get().toLowerCase());
  }

  function render() {
    const op = selectbox({
      source: opS,
      options: [
        { value: "includes" as const, label: "bevat" },
        { value: "notincludes" as const, label: "bevat niet" },
        { value: "eq" as const, label: "=" },
        { value: "neq" as const, label: "/=" },
      ],
    });

    const ms = textbox({ source: inputStrS });

    return (
      <div className={style.equal_columns}>
        <div style="max-width: 104px;">{op}</div>
        {ms}
      </div>
    );
  }

  const codec = c.Codec.interface({
    str: c.string,
    op: c.oneOf([
      c.exactly("eq"),
      c.exactly("neq"),
      c.exactly("includes"),
      c.exactly("notincludes"),
    ]),
  });

  function observe(cb: () => void): unsubber {
    const unsub1 = opS.observe(cb);
    const unsub2 = inputStrS.observe(cb);

    return () => {
      unsub1();
      unsub2();
    };
  }

  return {
    render,
    filter,
    observe,
    serializeState: () => {
      return codec.encode({ str: inputStrS.get(), op: opS.get() });
    },
    applySerializedState: (s) => {
      return codec.decode(s).caseOf({
        Left: (_) => false,
        Right: (r) => {
          opS.set(r.op);
          inputStrS.set(r.str);
          return true;
        },
      });
    },
  };
}

export function arrayFilter<FilterTarget>(
  singleElementFilter: Filter<FilterTarget>,
): Filter<FilterTarget[]> {
  type op = "includes" | "notincludes";
  const opS: Source<op> = new Source("includes");

  function filter(arr: FilterTarget[]) {
    const op = opS.get();
    return match(op)
      .with("includes", () =>
        arr.some((t) => singleElementFilter.filter(t) === true),
      )
      .with("notincludes", () =>
        arr.every((t) => singleElementFilter.filter(t) === false),
      )
      .exhaustive();
  }

  function render() {
    const op = selectbox({
      source: opS,
      options: [
        { value: "includes" as const, label: "bevat" },
        { value: "notincludes" as const, label: "bevat niet" },
      ],
    });

    return (
      <div className={style.equal_columns}>
        <div style="max-width: 104px;">{op}</div>
        {singleElementFilter.render()}
      </div>
    );
  }

  const codec = c.Codec.interface({
    single: c.unknown,
    op: c.oneOf([c.exactly("includes"), c.exactly("notincludes")]),
  });

  function observe(cb: () => void): unsubber {
    const unsub1 = opS.observe(cb);
    const unsub2 = singleElementFilter.observe(cb);

    return () => {
      unsub1();
      unsub2();
    };
  }

  return {
    render,
    filter,
    observe,
    serializeState: () => {
      return codec.encode({
        single: singleElementFilter.serializeState(),
        op: opS.get(),
      });
    },
    applySerializedState: (s) => {
      return codec.decode(s).caseOf({
        Left: (_) => false,
        Right: (r) => {
          opS.set(r.op);
          return singleElementFilter.applySerializedState(r.single);
        },
      });
    },
  };
}

const localDateC = c.Codec.custom<joda.LocalDate>({
  encode: (t: joda.LocalDate): string => {
    //return t.toString();
    return t.format(joda.DateTimeFormatter.ISO_LOCAL_DATE);
  },
  decode: (u: unknown): Either<string, joda.LocalDate> => {
    if (typeof u !== "string") {
      return Left(`Need string to decode date: ${JSON.stringify(u)}`);
    } else {
      try {
        return Right(joda.LocalDate.parse(u));
      } catch (err) {
        return Left(tryExtractErrorMessage(err));
      }
    }
  },
  schema: () => ({
    type: "string",
    format: "date",
  }),
});
